/**
 * FOLLOWER BOOST — Hero FX sequencer (premium++ layer, decorative only)
 *
 *  - live phone: follower counter ticks up, chart line follows (illustrative numbers)
 *  - per-network micro-interactions spawned one at a time, never in sync
 *  - occasional micro-notifications near the phone (no permanent rain)
 *  - icon entrances/exits + mobile rotation of hidden networks
 *
 * Everything is class/DOM toggling on timers; motion itself is CSS
 * (transform/opacity). Timers stop when the hero leaves the viewport or the
 * tab is hidden, and nothing runs under prefers-reduced-motion or on
 * low-power devices (few cores / save-data).
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const MOBILE = window.matchMedia('(max-width: 749px)');
const LOW_POWER =
  (navigator.connection && navigator.connection.saveData) ||
  (navigator.deviceMemory && navigator.deviceMemory <= 2) ||
  (MOBILE.matches && navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2);

const rand = (a, b) => a + Math.random() * (b - a);
const pick = (arr, not) => {
  if (arr.length < 2) return arr[0];
  let v;
  do v = arr[Math.floor(Math.random() * arr.length)];
  while (v === not);
  return v;
};
const fmtInt = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const fmtK = (n) => (n >= 1000 ? (n / 1000).toFixed(1).replace('.0', '').replace('.', ',') + 'K' : String(Math.round(n)));

class FbHeroFx {
  constructor(el) {
    this.el = el;
    this.scene = el.querySelector('[data-fb-scene]');
    this.orbits = [...el.querySelectorAll('.fb-orbit')];
    this.notifs = el.querySelector('[data-fb-notifs]');
    this.value = el.querySelector('[data-fb-live]');
    this.line = el.querySelector('[data-fb-live-line]');
    this.area = el.querySelector('[data-fb-live-area]');
    this.dot = el.querySelector('[data-fb-live-dot]');
    this.timers = [];
    this.running = false;
    this.lastFx = null;

    if (LOW_POWER) el.classList.add('is-low-power');
    if (REDUCED.matches || LOW_POWER) return;

    // chart state: 10 points (x fixed), y between 12 and 90 (SVG viewBox 240x110)
    this.ys = [82, 70, 74, 56, 62, 42, 46, 26, 16, 12];
    this.xs = [8, 34, 60, 86, 112, 138, 164, 190, 216, 232];
    this.live = this.value ? parseInt(this.value.dataset.fbLive, 10) || 0 : 0;
    this.liveStarted = false;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) this.start();
        else this.stop();
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else if (this.visible) this.start();
    });
    // remember visibility for the visibilitychange handler
    new IntersectionObserver((e) => (this.visible = e[0].isIntersecting), { threshold: 0.15 }).observe(el);
  }

  /* ---------- lifecycle ---------- */
  start() {
    if (this.running) return;
    this.running = true;
    // the counter starts after the entrance roll of fb-hero-3d.js (≈2.2s)
    this.schedule(() => this.tickCounter(), this.liveStarted ? rand(1200, 2400) : 2600);
    this.schedule(() => this.tickFx(), rand(1400, 2200));
    this.schedule(() => this.tickNotif(), rand(3800, 5200));
    this.schedule(() => this.tickPresence(), rand(5000, 7000));
  }

  stop() {
    this.running = false;
    this.timers.forEach(clearTimeout);
    this.timers = [];
  }

  schedule(fn, ms) {
    if (!this.running) return;
    const id = setTimeout(() => {
      this.timers = this.timers.filter((t) => t !== id);
      if (this.running) fn();
    }, ms);
    this.timers.push(id);
  }

  visibleOrbits() {
    return this.orbits.filter((o) => !o.classList.contains('is-away') && getComputedStyle(o).display !== 'none');
  }

  /* ---------- 6. live phone ---------- */
  tickCounter() {
    if (this.value && this.live) {
      this.liveStarted = true;
      this.live += Math.round(rand(3, 14));
      this.value.textContent = fmtInt(this.live);
      this.value.classList.remove('is-tick');
      void this.value.offsetWidth;
      this.value.classList.add('is-tick');
    }
    if (this.line && Math.random() < 0.55) this.pushPoint();
    this.schedule(() => this.tickCounter(), rand(1500, 3200));
  }

  pushPoint() {
    // the curve keeps trending up with small noise, bounded inside the card
    const last = this.ys[this.ys.length - 1];
    let next = last - rand(-4, 9);
    if (next < 10) next = rand(30, 48); // soft reset, still reads as growth
    next = Math.max(10, Math.min(92, next));
    this.ys = [...this.ys.slice(1), next];
    const pts = this.xs.map((x, i) => `${x} ${this.ys[i].toFixed(1)}`);
    const d = 'M' + pts.join(' L');
    this.line.setAttribute('d', d);
    if (this.area) this.area.setAttribute('d', `${d} L232 104 L8 104 Z`);
    if (this.dot) this.dot.setAttribute('cy', next.toFixed(1));
  }

  /* ---------- 5. per-network FX ---------- */
  tickFx() {
    const candidates = this.visibleOrbits().filter((o) => o.dataset.fx);
    const orbit = pick(candidates, this.lastFx);
    if (orbit) {
      this.lastFx = orbit;
      this.spawnFx(orbit);
    }
    this.schedule(() => this.tickFx(), rand(2000, 3600));
  }

  spawnFx(orbit) {
    const slot = orbit.querySelector('[data-fb-fx]');
    const tpl = this.el.querySelector(`[data-fb-fx-tpl="${orbit.dataset.fx}"]`);
    if (!slot || !tpl) return;
    slot.innerHTML = '';
    const frag = tpl.content.cloneNode(true);
    // hearts: stagger + spread
    frag.querySelectorAll('.fb-fx__heart').forEach((h, i) => {
      h.style.setProperty('--fx-d', `${i * 260}ms`);
      h.style.setProperty('--fx-x', `${rand(-16, 16).toFixed(0)}px`);
      h.style.setProperty('--fx-drift', `${rand(-14, 14).toFixed(0)}px`);
    });
    frag.querySelectorAll('.fb-fx__chip').forEach((c, i) => c.style.setProperty('--fx-d', `${420 + i * 700}ms`));
    frag.querySelectorAll('[data-fx-num]').forEach((b) => {
      const [lo, hi] = b.dataset.fxNum.split(',').map(Number);
      b.textContent = '+' + fmtK(rand(lo, hi));
    });
    slot.appendChild(frag);
    // the icon itself glows while its event plays (no separate ring/box)
    orbit.classList.add('is-fx');
    setTimeout(() => orbit.classList.remove('is-fx'), 1900);
    // self-clean after the longest animation
    setTimeout(() => {
      if (slot.firstChild) slot.innerHTML = '';
    }, 4200);
  }

  /* ---------- 7. micro-notifications near the phone ---------- */
  tickNotif() {
    const tpl = this.el.querySelector('[data-fb-notif-tpl]');
    if (this.notifs && tpl && !this.notifs.firstChild) {
      const items = [...tpl.content.children];
      const n = items[Math.floor(Math.random() * items.length)].cloneNode(true);
      n.style.top = `${rand(0, 40).toFixed(0)}%`;
      this.notifs.appendChild(n);
      setTimeout(() => n.remove(), 3200);
    }
    this.schedule(() => this.tickNotif(), rand(5500, 9000));
  }

  /* ---------- 4/8. entrances, exits, mobile rotation ---------- */
  tickPresence() {
    if (MOBILE.matches) {
      // rotate one hidden network in, one visible out (phone stays central)
      const pool = this.orbits.filter((o) => o.classList.contains('is-away') || getComputedStyle(o).display === 'none');
      const shown = this.visibleOrbits();
      if (pool.length && shown.length > 3) {
        const out = pick(shown);
        const inn = pool[Math.floor(Math.random() * pool.length)];
        out.classList.add('is-away');
        setTimeout(() => {
          out.classList.remove('is-cycled');
          inn.classList.add('is-cycled', 'is-away');
          requestAnimationFrame(() => requestAnimationFrame(() => inn.classList.remove('is-away')));
        }, 950);
      }
    } else {
      // desktop: one icon leaves briefly then comes back (never the front-lane badge holders)
      const shown = this.visibleOrbits().filter((o) => !o.querySelector('.fb-badge'));
      const o = pick(shown);
      if (o) {
        o.classList.add('is-away');
        setTimeout(() => o.classList.remove('is-away'), rand(2600, 4200));
      }
    }
    this.schedule(() => this.tickPresence(), rand(7000, 11000));
  }
}

const boot = () => {
  document.querySelectorAll('[data-fb-hero]').forEach((el) => {
    if (!el.__fbHeroFx) el.__fbHeroFx = new FbHeroFx(el);
  });
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('shopify:section:load', boot);
